// components/charts/StockKlineChart.jsx
// K-line candlestick chart with Volume + MA lines + prediction overlay
// Uses lightweight-charts library (same as PortfolioReviewPage)

import React, { useRef, useEffect, useState } from 'react';
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, AreaSeries } from 'lightweight-charts';
import { theme } from '../../theme';

// MA color scheme
const MA_COLORS = {
  5:   '#f59e0b',  // amber
  10:  '#3b82f6',  // blue
  60:  '#a855f7',  // purple
  120: '#ec4899',  // pink
  240: '#ef4444',  // red
};

// Calculate Simple Moving Average
function calculateMA(data, period) {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push({ time: data[i].time, value: undefined });
    } else {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) {
        sum += data[j].close;
      }
      result.push({ time: data[i].time, value: sum / period });
    }
  }
  return result.filter(d => d.value !== undefined);
}

export function StockKlineChart({ ticker, priceHistory, userPrediction, aiPrediction }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const [visibleMAs, setVisibleMAs] = useState({ 5: true, 10: true, 60: true, 120: true, 240: true });

  useEffect(() => {
    if (!chartContainerRef.current || !priceHistory || priceHistory.length === 0) return;

    // Cleanup previous chart
    try {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    } catch (e) {
      chartRef.current = null;
    }

    const containerWidth = chartContainerRef.current.clientWidth;

    try {
      // Create chart
      const chart = createChart(chartContainerRef.current, {
        width: containerWidth,
        height: 500,
        layout: {
          background: { color: theme.bg },
          textColor: theme.textDark,
          fontSize: 12,
        },
        grid: {
          vertLines: { color: '#1c2128' },
          horzLines: { color: '#1c2128' },
        },
        crosshair: { mode: 1 },
        rightPriceScale: {
          borderColor: theme.border,
          scaleMargins: { top: 0.1, bottom: 0.25 },
        },
        timeScale: {
          borderColor: theme.border,
          timeVisible: false,
          dayVisible: true,
        },
      });

      chartRef.current = chart;

      // Convert price history to chart format
      // Polygon timestamps are in milliseconds, lightweight-charts needs YYYY-MM-DD
      const candleData = priceHistory.map(p => {
        const d = new Date(p.date);
        const dateStr = d.getFullYear() + '-' +
          String(d.getMonth() + 1).padStart(2, '0') + '-' +
          String(d.getDate()).padStart(2, '0');
        return {
          time: dateStr,
          open: p.open,
          high: p.high,
          low: p.low,
          close: p.close,
        };
      });

      // Deduplicate by time (keep last entry per date)
      const deduped = {};
      candleData.forEach(c => { deduped[c.time] = c; });
      const uniqueCandleData = Object.values(deduped).sort((a, b) => a.time.localeCompare(b.time));

      const volumeData = priceHistory.map(p => {
        const d = new Date(p.date);
        const dateStr = d.getFullYear() + '-' +
          String(d.getMonth() + 1).padStart(2, '0') + '-' +
          String(d.getDate()).padStart(2, '0');
        return {
          time: dateStr,
          value: p.volume,
          color: p.close >= p.open ? 'rgba(34, 197, 94, 0.3)' : 'rgba(248, 81, 73, 0.3)',
        };
      });

      // Deduplicate volume data too
      const dedupedVol = {};
      volumeData.forEach(v => { dedupedVol[v.time] = v; });
      const uniqueVolumeData = Object.values(dedupedVol).sort((a, b) => a.time.localeCompare(b.time));

      // Add candlestick series
      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#22c55e',
        downColor: '#f85149',
        borderDownColor: '#f85149',
        borderUpColor: '#22c55e',
        wickDownColor: '#f85149',
        wickUpColor: '#22c55e',
      });
      candleSeries.setData(uniqueCandleData);

      // Add volume as histogram at bottom
      const volumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'volume' },
        priceScaleId: 'volume',
      });
      volumeSeries.priceScale().applyOptions({
        scaleMargins: { top: 0.8, bottom: 0 },
      });
      volumeSeries.setData(uniqueVolumeData);

      // Add MA lines
      const maPeriods = [5, 10, 60, 120, 240];

      maPeriods.forEach(period => {
        if (uniqueCandleData.length >= period) {
          const maData = calculateMA(uniqueCandleData, period);
          const maSeries = chart.addSeries(LineSeries, {
            color: MA_COLORS[period],
            lineWidth: period <= 10 ? 1 : 2,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
            visible: visibleMAs[period],
          });
          maSeries.setData(maData);
        }
      });

      // Add prediction overlay lines (if submitted)
      if (userPrediction || aiPrediction) {
        const lastCandle = uniqueCandleData[uniqueCandleData.length - 1];
        if (lastCandle) {
          const lastPrice = lastCandle.close;
          const lastTime = lastCandle.time;

          // Generate next 5 trading days for wider, more visible shading
          const parts = lastTime.split('-');
          const lastDate = new Date(+parts[0], +parts[1] - 1, +parts[2]);

          const futureDates = [];
          let curDate = new Date(lastDate);
          for (let i = 0; i < 5; i++) {
            curDate = new Date(curDate);
            curDate.setDate(curDate.getDate() + 1);
            while (curDate.getDay() === 0 || curDate.getDay() === 6) {
              curDate.setDate(curDate.getDate() + 1);
            }
            futureDates.push(
              curDate.getFullYear() + '-' +
              String(curDate.getMonth() + 1).padStart(2, '0') + '-' +
              String(curDate.getDate()).padStart(2, '0')
            );
          }

          const tierMultiplier = {
            'big_rise': 0.05,
            'small_rise': 0.02,
            'flat': 0,
            'small_drop': -0.02,
            'big_drop': -0.05,
          };

          if (userPrediction) {
            const mult = tierMultiplier[userPrediction] || 0;
            const targetPrice = lastPrice * (1 + mult);
            // Gradual line from current to target over 5 days
            const userArea = chart.addSeries(AreaSeries, {
              topColor: 'rgba(45, 212, 191, 0.45)',
              bottomColor: 'rgba(45, 212, 191, 0.08)',
              lineColor: theme.teal,
              lineWidth: 4,
              lineStyle: 2,
              priceLineVisible: false,
              lastValueVisible: true,
              crosshairMarkerVisible: false,
            });
            const mid1 = lastPrice + (targetPrice - lastPrice) * 0.3;
            const mid2 = lastPrice + (targetPrice - lastPrice) * 0.6;
            const mid3 = lastPrice + (targetPrice - lastPrice) * 0.85;
            userArea.setData([
              { time: lastTime, value: lastPrice },
              { time: futureDates[0], value: mid1 },
              { time: futureDates[1], value: mid2 },
              { time: futureDates[2], value: mid3 },
              { time: futureDates[3], value: targetPrice },
              { time: futureDates[4], value: targetPrice },
            ]);
          }

          if (aiPrediction) {
            const mult = tierMultiplier[aiPrediction] || 0;
            const targetPrice = lastPrice * (1 + mult);
            // Gradual line from current to target over 5 days
            const aiArea = chart.addSeries(AreaSeries, {
              topColor: 'rgba(168, 85, 247, 0.40)',
              bottomColor: 'rgba(168, 85, 247, 0.08)',
              lineColor: theme.purple,
              lineWidth: 4,
              lineStyle: 2,
              priceLineVisible: false,
              lastValueVisible: true,
              crosshairMarkerVisible: false,
            });
            const mid1 = lastPrice + (targetPrice - lastPrice) * 0.3;
            const mid2 = lastPrice + (targetPrice - lastPrice) * 0.6;
            const mid3 = lastPrice + (targetPrice - lastPrice) * 0.85;
            aiArea.setData([
              { time: lastTime, value: lastPrice },
              { time: futureDates[0], value: mid1 },
              { time: futureDates[1], value: mid2 },
              { time: futureDates[2], value: mid3 },
              { time: futureDates[3], value: targetPrice },
              { time: futureDates[4], value: targetPrice },
            ]);
          }
        }
      }

      chart.timeScale().fitContent();

      // Resize handler
      const handleResize = () => {
        if (chartContainerRef.current && chartRef.current) {
          chart.applyOptions({ width: chartContainerRef.current.clientWidth });
        }
      };
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        try {
          if (chartRef.current) {
            chartRef.current.remove();
            chartRef.current = null;
          }
        } catch (e) {
          chartRef.current = null;
        }
      };
    } catch (err) {
      console.error('Chart render error:', err);
    }
  }, [priceHistory, userPrediction, aiPrediction, visibleMAs]);

  const toggleMA = (period) => {
    setVisibleMAs(prev => ({ ...prev, [period]: !prev[period] }));
  };

  const TIER_LABELS = {
    'big_rise': 'Big Rise',
    'small_rise': 'Small Rise',
    'flat': 'Flat',
    'small_drop': 'Small Drop',
    'big_drop': 'Big Drop',
  };

  return (
    <div style={{
      background: theme.bgCard,
      border: '1px solid ' + theme.border,
      borderRadius: '14px 14px 0 0',
      borderBottom: 'none',
      padding: '24px 24px 12px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <h3 style={{ color: theme.text, fontSize: '22px', fontWeight: 600, margin: 0 }}>
          Price Action — {ticker}
        </h3>
        {/* Prediction legend */}
        {(userPrediction || aiPrediction) && (
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            {userPrediction && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: 'rgba(45, 212, 191, 0.12)',
                border: '1px solid rgba(45, 212, 191, 0.4)',
                borderRadius: '10px', padding: '8px 18px',
              }}>
                <div style={{ width: '28px', height: '5px', background: theme.teal, borderRadius: '3px' }} />
                <span style={{ color: theme.teal, fontSize: '18px', fontWeight: 700 }}>You: {TIER_LABELS[userPrediction]}</span>
              </div>
            )}
            {aiPrediction && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: 'rgba(168, 85, 247, 0.12)',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                borderRadius: '10px', padding: '8px 18px',
              }}>
                <div style={{ width: '28px', height: '5px', background: theme.purple, borderRadius: '3px' }} />
                <span style={{ color: theme.purple, fontSize: '18px', fontWeight: 700 }}>AI: {TIER_LABELS[aiPrediction]}</span>
              </div>
            )}
            <span style={{ color: theme.textDark, fontSize: '14px', fontStyle: 'italic' }}>Directional · Not price targets</span>
          </div>
        )}
      </div>

      {/* MA Toggle Bar */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {[5, 10, 60, 120, 240].map(period => (
          <button
            key={period}
            onClick={() => toggleMA(period)}
            style={{
              background: visibleMAs[period] ? MA_COLORS[period] + '20' : 'transparent',
              border: '1px solid ' + (visibleMAs[period] ? MA_COLORS[period] : theme.border),
              borderRadius: '6px',
              padding: '4px 10px',
              color: visibleMAs[period] ? MA_COLORS[period] : theme.textDark,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            MA{period}
          </button>
        ))}
      </div>

      {/* Chart Container */}
      <div ref={chartContainerRef} style={{ width: '100%', height: '500px', borderRadius: '8px', overflow: 'hidden' }} />

      {/* Prediction explanation */}
      {(userPrediction || aiPrediction) && (
        <div style={{ marginTop: '8px' }}>
          <p style={{ color: theme.textDark, fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
            Shaded areas show directional predictions (not price targets).
            <span style={{ color: theme.teal, fontWeight: 600 }}> Teal = You</span>,
            <span style={{ color: theme.purple, fontWeight: 600 }}> Purple = AI</span>.
          </p>
        </div>
      )}
    </div>
  );
}

export default StockKlineChart;
